/**
 * «Податкові декларації» (меню Кадри, 2026-07-30) — список поданих
 * податкових розрахунків ПДФО/ВЗ/ЄСВ (J05) зі статусом подання.
 *
 * Рішення власника: створення/підпис — єдина механіка Кабінету ДПС
 * (створити → чернетка у Журналі → підпис там), а ЦЯ сторінка — зручний
 * перелік своїх розрахунків без фільтрів Кабінету.
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { taxReportJ05, type TaxReport } from '@/api/taxReportJ05';
import PeriodFilter, { useHrPeriodFilter } from '@/shared/components/PeriodFilter';
import { orgs } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import type { ColDef } from 'ag-grid-community';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const MONTHS = ['', 'січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

const SUB_STATUS: Record<string, [string, string]> = {
  DRAFT: ['чернетка', '#64748b'],
  SIGNED: ['підписаний', '#7c3aed'],
  SENT: ['відправлений', '#1d4ed8'],
  DELIVERED: ['обробляється', '#0369a1'],
  ACCEPTED: ['прийнятий', '#16a34a'],
  REJECTED: ['не прийнятий', '#dc2626'],
  ERROR: ['помилка', '#dc2626'],
};

const REPORT_TYPES: Record<string, string> = {
  REPORTING: 'звітний',
  REPORTING_NEW: 'звітний новий',
  CORRECTING: 'уточнюючий',
};

export default function TaxReportsJ05Page() {
  const navigate = useNavigate();
  const { hasPermissionInAnyOrg, totalAccessibleOrgs } =
    usePermissionsByOrg('payroll.manage');

  // Фільтри під назвою сторінки (рішення власника 2026-08-19).
  const [orgId, setOrgId] = useState('');
  // Період: порожні поля → період шапки; заповнені → лише вони (ч.775).
  const per = useHrPeriodFilter();

  // 2026-08-23 (Норма #29): серверна пагінація/сортування; статус подання
  // (submission_status) і фільтри рік/місяць — на сервері.
  const refreshRef = useRef<(() => void) | null>(null);
  const { datasource, total } = useInfiniteGridDatasource<TaxReport>(
    (limit, offset, sort) => taxReportJ05.list({
      organization_id: orgId || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir, limit, offset,
    }),
    [orgId, ...per.key],
  );
  const orgListQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgListQ.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgListQ.data]);
  
  const cols = useMemo<ColDef<TaxReport>[]>(() => [
    {
      colId: 'period', headerName: 'Період', width: 150,
      valueGetter: (p) => !p.data ? '' : p.data.period_kind === 'MONTH'
        ? `${MONTHS[p.data.period_month ?? 0]} ${p.data.period_year}`
        : `${Math.ceil((p.data.period_month ?? 0) / 3)} кв. ${p.data.period_year}`,
    },
    {
      field: 'organization_id', headerName: 'Організація', flex: 1, minWidth: 200,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '') : '',
    },
    {
      field: 'report_type', headerName: 'Тип', width: 130,
      valueFormatter: (p) => p.data ? (REPORT_TYPES[p.value] ?? p.value) : '',
    },
    // ч.806: розрахунок за відокремлений підрозділ (лише 4ДФ).
    { field: 'activity_place_name', headerName: 'Підрозділ', width: 170, sortable: false },
    // № — номер РОЗРАХУНКУ в періоді (той, що йде у ДПС і в ім'я файлу).
    { field: 'report_ordinal', headerName: '№ розрахунку', width: 130 },
    {
      colId: 'submission', field: 'submission_status', headerName: 'Подання', width: 150,
      cellRenderer: (p: { data?: TaxReport }) => {
        if (!p.data) return '';
        const st = p.data.submission_status;
        if (!st) return <span style={{ color: '#94a3b8' }}>—</span>;
        const [txt, color] = SUB_STATUS[st] ?? [st, '#334155'];
        return <span style={{ color, fontWeight: st === 'ACCEPTED' ? 600 : 400 }}>{txt}</span>;
      },
    },
    {
      field: 'document_date', headerName: 'Дата', width: 130,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Податкові розрахунки (ПДФО / ВЗ / ЄСВ)<TitleTotal total={total} /></h2>
        <button className="primary" style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/tax-reports-j05/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `Немає прав у жодній з ${totalAccessibleOrgs} організацій`
            : 'Створити розрахунок — підпис і відправка у Кабінеті ДПС'}
        >
          + Створити
        </button>
      </div>
      <div className="filters">
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
                style={{ width: 240, flex: '0 0 auto' }}>
          <option value="">Усі організації</option>
          {orgListQ.data?.items.map((o) => (
            <option key={o.id} value={o.id}>{o.name} — {o.code}</option>
          ))}
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: 8 }}>
        Підпис КЕП і відправка — у Журналі Кабінету ДПС; тут — перелік
        розрахунків зі статусами подання.
      </div>
      <InfiniteDocGrid<TaxReport>
        datasource={datasource}
        columnDefs={cols}
        storageKey="tax-reports-j05"
        onRowDoubleClicked={(r) => navigate(`/tax-reports-j05/${r.id}`)}
        // ч.1316: «×» у колонці дій. Видаляється те, що ще НЕ пішло у ДПС (прочерк або
        // «чернетка») — рішення власника 2026-08-19; сервер тримає той самий гард (409).
        deleteRow={{
          remove: (id) => taxReportJ05.remove(id),
          canDelete: (r) => !r.submission_status || r.submission_status === 'DRAFT',
          whyNot: () => 'Розрахунок уже надіслано до ДПС — видалити не можна',
        }}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
        refreshRef={refreshRef}
      />
    </div>
  );
}
